import { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { presupuestosApi } from '../api/presupuestos'
import EstadoBadgePresupuesto, { COLORES_PRESUPUESTO, LABELS_PRESUPUESTO } from '../components/EstadoBadgePresupuesto'
import EstadoChipsFilter from '../components/EstadoChipsFilter'
import Boton from '../components/Boton'
import { useStoredState } from '../hooks/useStoredState'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { fmtFecha, fmtMonto } from '../utils/fecha'
import { colors, font, text, sx } from '../design/tokens'
import Paginador from '../components/Paginador'
import { RecorridoMini, ChipVersion } from '../components/RecorridoPresupuesto'

// Sin 'aprobado': el estado dejó de producirse el 16 sep 2026 (los presupuestos
// no requieren aprobación) y no hay ninguno en producción.
const CHIP_OPTIONS = ['borrador', 'enviado', 'aceptado', 'rechazado', 'vencido', 'convertido', 'perdido'].map(v => ({
  value: v,
  label: LABELS_PRESUPUESTO[v],
  bg: COLORES_PRESUPUESTO[v].bg,
  color: COLORES_PRESUPUESTO[v].color,
}))

export default function PresupuestosPage() {
  const navigate = useNavigate()
  const [filtros, setFiltros] = useStoredState('lgr.filtros.presupuestos', { estados: [], q: '' })
  const [data, setData] = useState({ data: [], current_page: 1, last_page: 1, total: 0 })
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)

  const cargar = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const { data: res } = await presupuestosApi.listar({
        page,
        estado: filtros.estados.join(',') || undefined,
        q: filtros.q || undefined,
      })
      setData(res)
    } finally {
      setLoading(false)
    }
  }, [page, filtros])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(cargar, 30000)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Presupuestos</h1>
        <Boton variante="primary" onClick={() => navigate('/presupuestos/nuevo')}>+ Nuevo presupuesto</Boton>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input
          placeholder="Buscar nº o cliente..."
          value={filtros.q}
          onChange={e => { setFiltros({ ...filtros, q: e.target.value }); setPage(1) }}
          style={{ padding: '7px 10px', border: `1px solid ${colors.textPlh}`, borderRadius: 6, fontSize: text.body, width: 230 }}
        />
        <EstadoChipsFilter
          options={CHIP_OPTIONS}
          selected={filtros.estados}
          onToggle={v => {
            setFiltros({
              ...filtros,
              estados: filtros.estados.includes(v) ? filtros.estados.filter(x => x !== v) : [...filtros.estados, v],
            })
            setPage(1)
          }}
        />
        {(filtros.estados.length > 0 || filtros.q) && (
          <Boton variante="ghost" sm onClick={() => { setFiltros({ estados: [], q: '' }); setPage(1) }}>Limpiar</Boton>
        )}
      </div>

      <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: text.body }}>
          <thead>
            <tr style={{ background: colors.bgSoft }}>
              {['Número', 'Cliente', 'Total', 'Versiones', 'Recorrido', 'Estado', 'Próxima acción', 'Vendedor', 'Válido hasta'].map(h => (
                <th key={h} style={{ ...sx.th, ...(h === 'Total' ? { textAlign: 'right' } : {}) }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && data.data.length === 0 && (
              <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: colors.textMeta }}>Cargando…</td></tr>
            )}
            {!loading && data.data.length === 0 && (
              <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: colors.textMeta }}>Sin presupuestos. Creá el primero con "+ Nuevo presupuesto".</td></tr>
            )}
            {data.data.map(p => (
              <tr
                key={p.id}
                onClick={() => navigate(`/presupuestos/${p.id}`)}
                title="Ver presupuesto"
                style={{ cursor: 'pointer', borderBottom: `1px solid ${colors.borderSoft}` }}
                onMouseEnter={e => e.currentTarget.style.background = colors.bgSoft}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <td style={{ padding: '10px 16px', ...sx.mono, whiteSpace: 'nowrap' }}>
                  {p.numero}
                  <div style={{ fontSize: text.sec, color: colors.textMeta }}>{fmtFecha(p.fecha)}</div>
                </td>
                <td style={{ padding: '10px 16px' }}>{p.cliente?.nombre || p.destinatario?.nombre || '—'}</td>
                {/* La versión de la fila; con opciones sin elegir no hay un valor definido (Martin, 11 oct 2026) */}
                <td style={{ padding: '10px 16px', ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {!p.total ? '—' : p.total.desde === p.total.hasta
                    ? fmtMonto(p.total.desde)
                    : <span style={{ fontFamily: font.sans, color: colors.textSec }} title="Opciones sin elegir: el valor depende de la que elija el cliente">Múltiple valor</span>}
                </td>
                {/* Un renglón por venta: las versiones del número, la vigente resaltada */}
                <td style={{ padding: '10px 16px', whiteSpace: 'nowrap', display: 'flex', gap: 4, alignItems: 'center', minHeight: 46 }}>
                  {(p.versiones || [{ version: p.version, id: p.id }]).map(v => (
                    <ChipVersion key={v.id} version={v.version} activa={v.id === p.id} />
                  ))}
                </td>
                <td style={{ padding: '10px 16px' }}><RecorridoMini nodos={p.recorrido?.nodos} title={p.recorrido?.proxima_accion?.label} /></td>
                <td style={{ padding: '10px 16px' }}><EstadoBadgePresupuesto estado={p.estado} /></td>
                <td style={{ padding: '10px 16px', fontSize: text.sec, fontWeight: 500, color: colors.text }} onClick={e => p.estado === 'convertido' && e.stopPropagation()}>
                  {p.estado === 'convertido' && p.nota_venta
                    ? <Link to={`/notas-venta/${p.nota_venta.id}`} style={{ ...sx.mono, color: colors.link, fontWeight: 600 }} title="Ver la nota de venta">{p.nota_venta.numero_lgr}</Link>
                    : (p.recorrido?.proxima_accion?.label || '—')}
                </td>
                <td style={{ padding: '10px 16px', color: colors.textSec }}>{p.vendedor?.nombre || '—'}</td>
                <td style={{ padding: '10px 16px', ...sx.mono, color: colors.textSec }}>{p.valido_hasta ? fmtFecha(p.valido_hasta) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Paginador
        pagina={data.current_page}
        totalPaginas={data.last_page}
        onCambiar={setPage}
        info={`${data.total} presupuestos`}
      />
    </div>
  )
}
